import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {buildChildrenWithProps} from '../helpers/';
import {WIZARD_CLASSNAME} from '../helpers/consts';
import Step from '../Step';
import './index.scss';

class Wizard extends PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            isLoading: false,
            currentStepIndex: 0,
            numOfSteps: 0
        };

        // will store all <Step> ids
        this._wizardSteps = [];

        this.navigateTo = this.navigateTo.bind(this);
        this.handleAsync = this.handleAsync.bind(this);
        this.updateStep = this.updateStep.bind(this);
        this.navigateNext = this.navigateNext.bind(this);
        this.navigateBack = this.navigateBack.bind(this);

        this.controls = {
            navigateTo: this.navigateTo,
            next: this.navigateNext,
            back: this.navigateBack
        };
    }

    componentWillMount() {
        const { currentStepId } = this.props,
            numOfSteps = this.countSteps(),
            customStep = currentStepId ? this._wizardSteps.findIndex((step) => step.id === currentStepId) : null,
            currentStep = customStep || 0;

        this.setState({
            numOfSteps,
            currentStepIndex: currentStep
        });
    }

    componentWillReceiveProps(nextProps) {
        const { currentStepId } = this.props;

        if (nextProps.currentStepId !== currentStepId) {
            const currentStepIndex = nextProps.currentStepId ? this._wizardSteps.findIndex((step) => step.id === nextProps.currentStepId) : this.state.currentStepIndex;

            if (currentStepIndex >= 0) {
                this.setState({ currentStepIndex });
            }
        }
    }

    /**
     * Get an initial count of all the components of type <Step>
     */
    countSteps() {
        let validSteps = 0;

        React.Children.forEach(this.props.children, (child) => {
            if (child !== null && child.type === Step) {

                // generate an id for each step
                const id = child.props.id || validSteps,
                    title = child.props.title || id;

                this._wizardSteps[validSteps] = {
                    id,
                    title
                };

                // count all valid <Step>
                validSteps++;
            }
        });

        return validSteps;
    }

    /**
     * build all child elements and grant the relevant props
     * to the custom library elements
     *
     */
    getChildren() {
        const {
            currentStepIndex,
            numOfSteps,
            isLoading
        } = this.state;

        return buildChildrenWithProps(this.props.children, {
            currentStep: this._wizardSteps[currentStepIndex],
            navigateTo: this.navigateTo,
            navigateNext: this.navigateNext,
            navigateBack: this.navigateBack,
            steps: this._wizardSteps,
            numOfSteps,
            isLoading
        });
    }

    /**
     * trigger navigation to the next step.
    */
    navigateNext() {
        const { currentStepIndex, numOfSteps } = this.state;
        const nextStepId = (currentStepIndex + 1 < numOfSteps) ?
            this._wizardSteps[currentStepIndex + 1].id :
            this._wizardSteps[currentStepIndex].id;

        this.navigateTo(nextStepId);
    }

    /**
     * trigger navigation to the previous step.
    */
    navigateBack() {
        const { currentStepIndex } = this.state;
        const nextStepId = currentStepIndex > 0 ?
            this._wizardSteps[currentStepIndex - 1].id :
            this._wizardSteps[currentStepIndex].id;

        this.navigateTo(nextStepId);
    }

    /**
     * triggered once the user clicks on 'next' or 'back' button
     *
     * @param requestedStepId
     */
    navigateTo(requestedStepId) {

        const requestedStepIndex = this._wizardSteps.findIndex((currentStep) => currentStep.id === requestedStepId);

        if (requestedStepIndex === -1) {
            throw new Error('The requested step id does not exists.');
        }

        const {
            currentStepIndex,
            numOfSteps
        } = this.state;

        const { onStepSubmit } = this.props;

        const async = typeof onStepSubmit !== 'undefined',
            isLastStep = currentStepIndex + 1 === numOfSteps,
            isFirstStep = currentStepIndex === 0;

        // build props for all events
        const stepProps = {
            currentStepIndex,
            requestedStepIndex,
            currentStep: this._wizardSteps[currentStepIndex],
            requestedStep: this._wizardSteps[requestedStepIndex],
            isFirstStep,
            isLastStep
        };

        if (async) {
            this.handleAsync(stepProps);
            return;
        }

        this.updateStep(stepProps);
    }

    /**
     * Fire the proper callbacks and handle the new step
     * switch after synced/Asynced operation
     *
     * @param {} stepProps
     */
    updateStep(stepProps) {
        const {
            onComplete,
            onStepComplete
        } = this.props;

        const {
            isFirstStep,
            isLastStep,
            currentStepIndex,
            requestedStepIndex
        } = stepProps;

        // check the navigation direction so we can ignore the request if needed
        const isForward = requestedStepIndex >= currentStepIndex;

        // step completed event
        onStepComplete(stepProps);

        // wizard completed event
        if (isLastStep && isForward) {
            onComplete();
            return;
        }

        // if the user click 'Back' on the first step
        // or 'Next' on the last step then do nothing.
        if ((isFirstStep && !isForward) || (isLastStep && isForward)) {
            return;
        }

        // go to step
        this.setState({currentStepIndex: requestedStepIndex});
    }

    /**
     * Looking for 'onStepSubmit' callback and expects a promise
     * to resolve before navigating to the next page
     *
     * @param {} stepProps
     */
    handleAsync(stepProps) {
        const { onStepSubmit } = this.props;

        this.setState({isLoading: true});

        onStepSubmit(stepProps)
            .then(() => {
                this.setState({isLoading: false});
                this.updateStep(stepProps);
            })
            .catch(() => this.setState({isLoading: false}));
    }

    render() {
        const classes = [WIZARD_CLASSNAME, this.props.className].join(' ').trim();

        return (
            <div className={classes}>
                {this.getChildren()}
            </div>
        );
    }
}

Wizard.propTypes = {
    onStepComplete: PropTypes.func,
    onStepSubmit: PropTypes.func,
    onComplete: PropTypes.func,
    className: PropTypes.string,
    currentStepId: PropTypes.string,
    children: (props, propName) => {
        const prop = props[propName],
            stepsCount = React.Children.toArray(prop).some(({ type }) => type === Step);

        return stepsCount ? null : new Error('`<Wizard>` must have at least one `<Step/>` component.');
    }
};

Wizard.defaultProps = {
    onStepComplete: () => null,
    onComplete: () => null
};

export default Wizard;
